import { Spacing } from '@persona-id/design-tokens';
import { omit } from 'lodash/fp';
import styled, { css } from 'styled-components';

import Button, { ButtonProps } from 'design-system/components/Button';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';

export const NAVBAR_ITEM_HEIGHT = 32;

export const editorNavItemTruncateCSS = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TruncatedContainer = styled.span`
  ${editorNavItemTruncateCSS}
`;

export const EditorNavItemBase = styled(Button).attrs(() => ({
  variant: 'primary' as Pick<ButtonProps, 'variant'>,
  type: 'button' as Pick<ButtonProps, 'type'>,
}))<{ active?: boolean; isVisible?: boolean }>`
  ${paragraphMixins.mdBold};
  ${menuItemMixins.default};

  font-weight: normal;
  padding: ${Spacing[1]};
  color: ${({ theme }) => theme.color.text.secondary};
  svg *[stroke] {
    stroke: ${({ theme }) => theme.color.text.secondary};
  }
  white-space: break-spaces;
  text-align: left;

  & > div {
    justify-content: start;
    display: inline-block;
    ${editorNavItemTruncateCSS}
    ${({ prefixIcon, suffixIcon }) =>
      (prefixIcon != null || suffixIcon != null) &&
      css`
        display: flex;
      `}
  }

  &${isHovered}${isNotDisabled}, &${isFocused}${isNotDisabled} {
    ${menuItemMixins.hover};
  }

  &${isActive} {
    ${menuItemMixins.active};
  }

  &${isHovered}${isNotDisabled}, &${isFocused}${isNotDisabled}, &${isActive}${isNotDisabled} {
    box-shadow: none !important;
  }

  // use the pseudo-class directly instead of the mixin
  // because an alternative definition is created and overrides
  // these default stylings
  &:disabled {
    ${menuItemMixins.default};
    // Show active styles despite being disabled
    &${isActive} {
      ${menuItemMixins.active};
    }
  }

  ${({ prefixIcon }) =>
    !prefixIcon &&
    css`
      padding-left: ${Spacing[3]};
    `}
`;

const EditorNavItem = (props: ButtonProps & { active?: boolean; isVisible?: boolean }) => {
  const ChildContainer =
    props.prefixIcon || props.suffixIcon ? (
      <TruncatedContainer>{props.children}</TruncatedContainer>
    ) : (
      props.children
    );
  return (
    <EditorNavItemBase {...omit(['children', 'type'], props)}>{ChildContainer}</EditorNavItemBase>
  );
};

export default EditorNavItem;
